import React, { useState } from 'react';
import { AuthService } from '../services/AuthService';
import { useAuth } from '../contexts/AuthContext';
import { persistentLogger } from '../utils/PersistentLogger';
import './Login.css';

interface LoginProps {
  onLoginSuccess: () => void | Promise<void>;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const { login } = useAuth();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    persistentLogger.log('Login', 'Form submitted', { name });
    setError(null);
    setLoading(true);

    try {
      persistentLogger.log('Login', 'Sending login request to /api/auth/login');
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, password }),
      });

      const data = await response.json();
      persistentLogger.log('Login', 'Login response received', { 
        ok: response.ok, 
        status: response.status,
        hasToken: !!data.token,
        allowedFolders: data.allowedFolders,
        accessLevel: data.accessLevel
      });

      if (!response.ok) {
        persistentLogger.error('Login', 'Login failed', { error: data.error });
        throw new Error(data.error || 'Login failed');
      }

      persistentLogger.log('Login', 'Storing token in AuthService');
      AuthService.setAuth(data.token, data.name, data.allowedFolders, data.accessLevel);

      persistentLogger.log('Login', 'Calling onLoginSuccess() callback');
      await onLoginSuccess();

      persistentLogger.log('Login', 'Calling login() from AuthContext');
      login(data.token, data.name, data.allowedFolders, data.accessLevel);
      persistentLogger.log('Login', 'Login flow completed successfully');
    } catch (err) {
      persistentLogger.error('Login', 'Login error', { error: err });
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Book Admin</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="login-button">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;

